import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Check } from '@mui/icons-material';
import { Avatar, IconButton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { settingsSections } from '../../../constants';
import { useAuthContext } from '../../../../../data-access';
import { AccountActions } from '../../../../SideBar/SettingsModal/SubscriptionSettings';
import { ReactComponent as EditIcon } from '../../../../../assets/tabs/settings-tab/edit-icon.svg';
import './ProfileSection.scss';

const MAX_NAME_CHARACTERS = 20;

const normalizeName = (value: string): string => {
  return value.trim();
};

const getCharacterCount = (value: string): number => {
  return normalizeName(value).length;
};

const ProfileSection: FC = () => {
  const { t } = useTranslation();
  const section = settingsSections.find((item) => item.key === 'account');
  const { user, updateUser } = useAuthContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState(user?.name ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (isEditing) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) {
      setName((user?.name ?? '').slice(0, MAX_NAME_CHARACTERS));
    }
  }, [isEditing, user?.name]);

  const handleCancel = useCallback(() => {
    if (isSaving) {
      return;
    }

    setIsEditing(false);
    setName((user?.name ?? '').slice(0, MAX_NAME_CHARACTERS));
  }, [isSaving, user?.name]);

  const handleSave = useCallback(async () => {
    if (isSaving) {
      return;
    }

    const rawName = inputRef.current?.value ?? name;
    const normalizedName = normalizeName(rawName);

    if (!normalizedName || getCharacterCount(rawName) > MAX_NAME_CHARACTERS) {
      return;
    }

    setIsSaving(true);

    try {
      setName(normalizedName);
      await updateUser({ name: normalizedName });
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, name, updateUser]);

  const handleNameChange = useCallback((value: string) => {
    setName(value.slice(0, MAX_NAME_CHARACTERS));
  }, []);

  const handleNameKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }
    },
    [handleCancel]
  );

  const handleNameFormSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      void handleSave();
    },
    [handleSave]
  );

  const handleNameBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const nextFocusedNode = event.relatedTarget as Node | null;

      if (nextFocusedNode && formRef.current?.contains(nextFocusedNode)) {
        return;
      }

      handleCancel();
    },
    [handleCancel]
  );

  const handleEditClick = useCallback(() => {
    if (!isEditing) {
      setName((user?.name ?? '').slice(0, MAX_NAME_CHARACTERS));
      setIsEditing(true);
    }

    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }, [isEditing, user?.name]);

  const isNameValid = !!normalizeName(name);
  const inputCharacterWidth = Math.max(name.length + 1, 2);
  const avatarChars = user?.name
    ?.split(' ')
    .map((word) => word.charAt(0))
    .join('');

  if (!section) {
    return null;
  }

  return (
    <Stack gap={2}>
      <Typography variant="h3" className="account-section__title">
        {t('settings.nav.profile')}
      </Typography>
      <Stack direction="row" alignItems="center" gap={2} className="account-section">
        {user?.photoUrl ? (
          <Avatar src={user.photoUrl} alt={user.name} className="account-section__avatar" />
        ) : (
          <Avatar alt={user?.name} className="account-section__avatar">
            {avatarChars}
          </Avatar>
        )}

        <form ref={formRef} onSubmit={handleNameFormSubmit} className="account-section__form">
          <Stack className="account-section__identity" gap={0.5}>
            <Stack direction="row" alignItems="center" gap={1}>
              {isEditing ? (
                <input
                  ref={inputRef}
                  className="account-section__input"
                  value={name}
                  onChange={(event) => handleNameChange(event.target.value)}
                  maxLength={MAX_NAME_CHARACTERS}
                  onKeyDown={handleNameKeyDown}
                  onBlur={handleNameBlur}
                  disabled={isSaving}
                  style={{ width: `${inputCharacterWidth}ch` }}
                />
              ) : (
                <Typography variant="h3" className="account-section__name">
                  {user?.name}
                </Typography>
              )}

              <IconButton type="button" onClick={handleEditClick} disabled={isSaving}>
                <EditIcon />
              </IconButton>

              {isEditing && (
                <IconButton type="submit" disabled={isSaving || !isNameValid}>
                  <Check fontSize="small" />
                </IconButton>
              )}
            </Stack>
            {user?.email ? (
              <Typography variant="body2" className="account-section__email">
                {user.email}
              </Typography>
            ) : null}
          </Stack>
        </form>
      </Stack>

      <div className="account-section__actions-block">
        <AccountActions className="account-section__actions" />
      </div>
    </Stack>
  );
};

export default ProfileSection;
